import { useState } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from '@/components/SEO';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';

const faqCategories = [
  {
    category: 'General Questions About Quick Free Invoice Tool',
    faqs: [
      {
        q: 'Is Quick Free Invoice Tool really 100% free?',
        a: 'Yes. You can create, customise, download, and print unlimited invoices and quotations completely free — no credit card required, no sign-up needed. Simply fill in your details and hit download. We offer an optional Pro upgrade for advanced features like real-time client read receipts and analytics, but the core invoicing tool is always free.',
      },
      {
        q: 'Do I need to create an account to use the free invoice generator?',
        a: 'No. You can create and download professional PDF and Word invoices instantly without registering. An optional free account lets you save your invoices, reuse templates, and access your billing history from any device.',
      },
      {
        q: 'Is my invoice data safe and private?',
        a: 'Yes. We do not sell your data to third parties, we do not use tracking across websites, and your invoice data is encrypted. See our Privacy Policy and Security pages for full details.',
      },
      {
        q: 'Who is Quick Free Invoice Tool designed for?',
        a: 'Our free invoice generator is built for freelancers, independent contractors, consultants, creative agencies, trade businesses, e-commerce sellers, tutors, healthcare practitioners, and any small business that needs to create professional invoices fast.',
      },
      {
        q: 'What countries does Quick Free Invoice Tool support?',
        a: 'Quick Free Invoice Tool works globally. We support 170+ currencies including USD, EUR, GBP, AUD, CAD, INR, MYR, SGD, and more. Our tax system covers VAT (EU/UK), GST (Australia, Canada, India, Singapore), SST (Malaysia), and US sales tax.',
      },
    ],
  },
  {
    category: 'Creating Invoices & Quotations',
    faqs: [
      {
        q: 'How do I create a professional invoice for free?',
        a: 'Open the Quick Free Invoice Tool homepage. Fill in your business name, address, client details, invoice date, due date, and line items (description, quantity, rate). Add any applicable taxes (VAT/GST/SST). Click Download to get a PDF or Word file. The whole process takes under 60 seconds.',
      },
      {
        q: 'Can I create a price quotation or estimate as well as an invoice?',
        a: 'Yes! Our Quotation Builder works exactly like the invoice builder. You can create professional quotations and estimates, share them with clients via a link, and convert approved quotations into final invoices with one click.',
      },
      {
        q: 'How do I add my business logo to an invoice?',
        a: 'In the invoice builder, click the logo upload area at the top of your invoice. Upload a PNG or JPG image of your logo and it will appear on all downloaded invoices. Your logo is stored securely if you have an account.',
      },
      {
        q: 'Can I set payment terms like Net 30 or Net 60 on my invoice?',
        a: 'Yes. In the invoice builder, you can set the payment due date directly, which effectively creates Net 15, Net 30, Net 60, or any custom payment window. You can also add a notes section to specify exact payment terms text.',
      },
      {
        q: 'How many line items can I add to an invoice?',
        a: 'There is no limit on line items. You can add as many products, services, hours, or materials as needed. Each line item supports a description, quantity, unit price, and optional per-line tax rate.',
      },
    ],
  },
  {
    category: 'Taxes: VAT, GST, SST & Sales Tax',
    faqs: [
      {
        q: 'How do I add VAT to my invoice?',
        a: 'In the invoice builder, find the tax section and enter your VAT rate (e.g. 20% for UK standard rate). The tool will calculate the VAT amount automatically and display it clearly on the invoice as required by HMRC. You can also enter your VAT registration number.',
      },
      {
        q: 'Does Quick Free Invoice Tool support GST (Goods and Services Tax)?',
        a: 'Yes. Whether you need Australian GST (10%), Canadian GST (5% federal), Indian GST (5/12/18/28%), or Singapore GST (9%), simply enter the applicable rate in the tax field and our invoice generator calculates the correct amount automatically.',
      },
      {
        q: 'Can I add multiple tax rates to a single invoice?',
        a: 'Yes. Quick Free Invoice Tool supports multiple tax tiers on one invoice — for example, a standard-rate VAT line and a reduced-rate VAT line for mixed supplies. Each tax line shows separately in the invoice total breakdown.',
      },
      {
        q: 'How do I apply SST for Malaysian invoices?',
        a: 'Select MYR as your currency and enter the applicable SST rate in the tax field. Malaysia charges Sales Tax (5% or 10% depending on goods) and Service Tax (6%). You can add both as separate tax lines if needed.',
      },
      {
        q: 'Can I create a zero-rated or tax-exempt invoice?',
        a: 'Yes. Simply leave the tax rate at 0% or remove the tax line entirely. You can add a note to the invoice stating "Zero-rated supply" or "Tax Exempt" as required by your local tax authority.',
      },
    ],
  },
  {
    category: 'Downloads, PDF & Word Exports',
    faqs: [
      {
        q: 'How do I download my invoice as a PDF?',
        a: 'Click the "Download PDF" button in the invoice builder or preview screen. Your browser will download a professionally formatted PDF invoice immediately — no watermarks, no restrictions, completely free.',
      },
      {
        q: 'Can I download my invoice as a Word (.docx) file?',
        a: 'Yes. Click the "Download Word" or "Download .docx" button to get an editable Microsoft Word version of your invoice. This lets you make further edits or store it in your accounting system.',
      },
      {
        q: 'Can I print my invoice directly?',
        a: 'Yes. Click the Print button or use the "Print" option in the invoice preview. Our invoices use print-optimised CSS so the layout looks perfect on paper at A4 or Letter size.',
      },
      {
        q: 'Why does my downloaded PDF look different from the preview?',
        a: 'Minor differences can occur due to fonts or browser rendering. For best results, use Chrome or Edge. If you see layout issues, try the Word export and convert to PDF using Microsoft Word for consistent results.',
      },
    ],
  },
  {
    category: 'Pro Features & Subscriptions',
    faqs: [
      {
        q: 'What is included in the Quick Free Invoice Tool Pro plan?',
        a: 'Pro unlocks: real-time client read receipts (know when your invoice is opened), a full analytics dashboard (outstanding balances, overdue invoices, client payment rankings), priority email support, and advanced invoice tracking. See the Pricing page for full details.',
      },
      {
        q: 'How do I upgrade to Pro?',
        a: 'Create a free account, then visit the Pricing page or your Dashboard and click Upgrade to Pro. Payment is processed securely via Stripe. You can cancel anytime.',
      },
      {
        q: 'Can I cancel my Pro subscription?',
        a: 'Yes. You can cancel anytime from your Account settings. Your Pro features remain active until the end of your current billing period.',
      },
      {
        q: 'Is there a free trial for Pro?',
        a: 'Check our Pricing page for current trial offers. The free plan is always available with unlimited invoice and quotation creation at no cost.',
      },
    ],
  },
];

const allFaqs = faqCategories.flatMap((c) => c.faqs);

const faqStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  name: 'Frequently Asked Questions — Quick Free Invoice Tool',
  description:
    'Common questions and answers about Quick Free Invoice Tool — the free online invoice generator for freelancers and small businesses.',
  url: 'https://quickfreeinvoicetool.com/faq',
  mainEntity: allFaqs.map((faq) => ({
    '@type': 'Question',
    name: faq.q,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.a,
    },
  })),
};

function AccordionItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <button
        className="w-full flex items-center justify-between px-5 py-4 text-left bg-white hover:bg-slate-50 transition-colors"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <span className="font-semibold text-slate-900 text-sm leading-snug pr-4">{q}</span>
        {open ? (
          <ChevronUp className="h-4 w-4 text-indigo-500 shrink-0" />
        ) : (
          <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />
        )}
      </button>
      {open && (
        <div className="px-5 pb-4 pt-0 bg-white border-t border-slate-100">
          <p className="text-sm text-slate-600 leading-relaxed">{a}</p>
        </div>
      )}
    </div>
  );
}

export default function FAQ() {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="FAQ — Frequently Asked Questions About Quick Free Invoice Tool | Free Invoice Generator"
        description="Get answers to frequently asked questions about Quick Free Invoice Tool. Learn about creating free invoices, adding VAT/GST taxes, downloading PDFs, Pro features, and more. Free invoice generator FAQ."
        keywords="invoice FAQ, free invoice generator questions, how to create invoice FAQ, VAT invoice questions, GST invoice help, invoice pdf download FAQ, quotation to invoice FAQ, invoice tool questions, quick free invoice FAQ"
        canonical="https://quickfreeinvoicetool.com/faq"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqStructuredData) }}
      />

      {/* Hero */}
      <section className="bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-700 text-white py-20">
        <div className="container mx-auto px-4 text-center max-w-3xl">
          <Badge className="bg-white/20 hover:bg-white/30 text-white border-white/30 mb-4 px-3 py-1 text-xs font-semibold uppercase tracking-wider rounded-full">
            FAQs
          </Badge>
          <h1 className="font-heading text-4xl md:text-5xl font-extrabold mb-6 leading-tight">
            Frequently Asked Questions — Quick Free Invoice Tool
          </h1>
          <p className="text-emerald-100 text-lg">
            Find fast answers to the most common questions about our free invoice generator, tax calculations, PDF downloads, quotations, and Pro features.
          </p>
        </div>
      </section>

      {/* FAQ Sections */}
      <section className="py-20">
        <div className="container mx-auto px-4 max-w-3xl">
          {faqCategories.map((cat) => (
            <div key={cat.category} className="mb-14">
              <h2 className="font-heading text-2xl font-bold text-slate-900 mb-6 pb-3 border-b border-slate-200">
                {cat.category}
              </h2>
              <div className="space-y-3">
                {cat.faqs.map((faq) => (
                  <AccordionItem key={faq.q} q={faq.q} a={faq.a} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Still have questions */}
      <section className="py-20 bg-slate-50 border-t">
        <div className="container mx-auto px-4 max-w-3xl text-center">
          <h2 className="font-heading text-3xl font-bold mb-4 text-slate-900">
            Didn't Find Your Answer? Contact Quick Free Invoice Tool Support
          </h2>
          <p className="text-slate-500 mb-8 text-lg">
            Our support team is available to help with any invoicing questions. Send us an email and we'll respond within 24 business hours.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="mailto:support@quickfreeinvoicetool.com">
              <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold">
                Email Support
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </a>
            <Link to="/help">
              <Button size="lg" variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                Browse Help Center
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
